import {
  createAction,
  createAsyncThunk,
  createSelector,
  createSlice} from '@reduxjs/toolkit'
import { pathOr } from 'ramda'
import { RootState } from 'store'

import { AssetsRepository } from 'services/repositories'
import {
  IAsset,
  IAssetsProductCategories,
  IAssetsQueryPayload,
  IAssetsTypes,
  IBrand,
  IEditAssetPayload,
  IGetProductsCategoriesAssets,
  // IEditAssetsForm,
  IPagination
} from 'types/assets'
import { IAssetsState } from 'types/slices'
import { LoadingStatus, TLoadingStatus } from 'types/user'
import { handleApiErrors } from 'utils/errors'
import { executeWithTransientRetries } from 'utils/transientHttpRetry'

const INITIAL_ASSET = {
  id: '',
  name: '',
  isActive: false,
  imageUrl: '',
  largeImageUrl: '',
  sourceGraphicFileFileName: '',
  sourceGraphicFileUrl: '',
  country: '',
  createdAt: '',
  updatedAt: '',
  assetTypeName: '',
  isDraft: false,
  assetType: {
    id: '',
    name: '',
    maxNumberPerProduct: 0,
    orderInProductCategory: 0,
    additionalFieldsConfig: '',
    createdAt: '',
    updatedAt: '',
    deletedAt: '',
    productCategory: {
      id: '',
      name: '',
      createdAt: '',
      updatedAt: '',
      deletedAt: '',
      brand: {
        id: '',
        name: '',
        createdAt: '',
        updatedAt: '',
        deletedAt: '',
        productCategories: []
      }
    }
  }
}
const INITIAL_PRODUCTS_CATEGORIES_ASSETS = {
  assetTypes: [],
  brand: {
    createdAt: '',
    updatedAt: '',
    deletedAt: '',
    id: '',
    name: '',
    packshotRequired: true
  },
  createdAt: '',
  updatedAt: '',
  deletedAt: '',
  id: '',
  name: ''
}

const initialState: IAssetsState = {
  status: 'initial',
  listStatus: 'initial',
  assets: [],
  brands: [],
  assetsTypes: [],
  productsCategoriesNames: [],
  productsCategoriesAssets: INITIAL_PRODUCTS_CATEGORIES_ASSETS,
  asset: INITIAL_ASSET,
  pagination: {
    page: 0,
    pagesTotal: 0,
    recordsTotal: 0,
    take: 0
  }
}

export const types = {
  getAssets: 'assets/getAssets',
  getBrandsAssets: 'assets/getBrandsAssets',
  getAssetsTypesByProductCategoryId: 'assets/getAssetsTypesByProductCategoryId',
  getAssetById: 'assets/getAssetById',
  createAsset: 'assets/createAsset',
  editAsset: 'assets/editAsset',
  saveAssetWithScope: 'assets/saveAssetWithScope',
  copyAsset: 'assets/copyAsset',
  deleteAsset: 'assets/deleteAsset',
  setAssetBlocked: 'assets/setAssetBlocked',
  assignAssetAllowedEditor: 'assets/assignAssetAllowedEditor',
  acceptDraftAsset: 'assets/acceptDraftAsset',
  getProductsCategoriesNames: 'assets/getProductsCategoriesNames',
  getProductsCategoriesAssets: 'assets/getProductsCategoriesAssets',
  clearEditedAssetOnRedux: 'clearEditedAssetOnRedux'
} as const

/** Ignore out-of-order `getAssets` responses when filters change quickly. */
let getAssetsRequestSeq = 0

export const getAssets = createAsyncThunk(
  types.getAssets,
  async (query: IAssetsQueryPayload, { rejectWithValue }) => {
    const requestSeq = ++getAssetsRequestSeq

    try {
      const response = await AssetsRepository.getAssets({ query })

      return { ...response.data, requestSeq }
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const getBrandsAssets = createAsyncThunk(
  types.getBrandsAssets,
  async (_, { rejectWithValue }) => {
    try {
      const response = await AssetsRepository.getBrandsAssets()

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const getAssetsTypesByProductCategoryId = createAsyncThunk(
  types.getAssetsTypesByProductCategoryId,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await AssetsRepository.getAssetsTypesByProductCategoryId(
        id
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const getAssetById = createAsyncThunk(
  types.getAssetById,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await AssetsRepository.getAssetById(id)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const createAsset = createAsyncThunk(
  types.createAsset,
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await AssetsRepository.createAsset(formData)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const deleteAsset = createAsyncThunk(
  types.deleteAsset,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await AssetsRepository.deleteAsset(id)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const editAsset = createAsyncThunk(
  types.editAsset,
  async (payload: IEditAssetPayload, { rejectWithValue }) => {
    const { id, formData } = payload

    try {
      const response = await AssetsRepository.editAsset(id, formData)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const saveAssetWithScope = createAsyncThunk(
  types.saveAssetWithScope,
  async (payload: IEditAssetPayload, { rejectWithValue }) => {
    const { id, formData, mode = 'all', productIds } = payload

    try {
      const response = await AssetsRepository.saveAssetWithScope(id, formData, {
        mode,
        productIds
      })

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data ?? err)
    }
  }
)

export const copyAsset = createAsyncThunk(
  types.copyAsset,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await AssetsRepository.copyAsset(id)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const setAssetBlocked = createAsyncThunk(
  types.setAssetBlocked,
  async (
    payload: { id: string; isBlocked: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await AssetsRepository.setAssetBlocked(
        payload.id,
        payload.isBlocked
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data ?? err)
    }
  }
)

export const assignAssetAllowedEditor = createAsyncThunk(
  types.assignAssetAllowedEditor,
  async (payload: { id: string; email: string }, { rejectWithValue }) => {
    try {
      const response = await AssetsRepository.assignAssetAllowedEditor(
        payload.id,
        payload.email
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data ?? err)
    }
  }
)

export const acceptDraftAsset = createAsyncThunk(
  types.acceptDraftAsset,
  async (id: string, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any
      const userId = state.auth.user?.id

      if (!userId) {
        return rejectWithValue('User ID not found')
      }

      const response = await AssetsRepository.acceptDraftAsset(id, userId)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? err)
    }
  }
)

export const getProductsCategoriesNames = createAsyncThunk(
  types.getProductsCategoriesNames,
  async (_, { rejectWithValue }) => {
    try {
      const response = await AssetsRepository.getProductsCategoriesNames()

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

/** Ignore out-of-order category-asset responses when switching products quickly. */
let getProductsCategoriesAssetsRequestSeq = 0

export const getProductsCategoriesAssets = createAsyncThunk(
  types.getProductsCategoriesAssets,
  async (payload: IGetProductsCategoriesAssets, { rejectWithValue }) => {
    const { id, country } = payload
    const requestSeq = ++getProductsCategoriesAssetsRequestSeq

    const result = await executeWithTransientRetries(() =>
      AssetsRepository.getProductsCategoriesAssets(id, country).then(
        res => res.data
      )
    )

    if (result.ok) {
      return {
        data: result.data,
        requestSeq,
        requestedCategoryId: id
      }
    }

    return rejectWithValue(result.error)
  }
)

export const clearEditedAssetOnRedux = createAction(
  types.clearEditedAssetOnRedux
)

export const assetsSlice = createSlice({
  name: 'assets',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAssets.pending, state => {
        state.listStatus = LoadingStatus.loading
      })
      .addCase(getAssets.fulfilled, (state, action) => {
        if (action.payload.requestSeq !== getAssetsRequestSeq) {
          return
        }
        state.listStatus = LoadingStatus.loaded
        state.assets = action.payload.data
        state.pagination = action.payload.meta
      })
      .addCase(getAssets.rejected, (state, error) => {
        state.listStatus = LoadingStatus.failed
        handleApiErrors(error, false)
      })
      .addCase(getAssetById.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getAssetById.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.asset = action.payload
      })
      .addCase(getAssetById.rejected, (state, error) => {
        state.status = LoadingStatus.failed
        handleApiErrors(error, false)
      })
      .addCase(getBrandsAssets.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getBrandsAssets.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.brands = action.payload
      })
      .addCase(getBrandsAssets.rejected, (state, error) => {
        state.status = LoadingStatus.failed
        handleApiErrors(error, false)
      })
      .addCase(getAssetsTypesByProductCategoryId.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getAssetsTypesByProductCategoryId.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.assetsTypes = action.payload
      })
      .addCase(getAssetsTypesByProductCategoryId.rejected, (state, error) => {
        state.status = LoadingStatus.failed
        handleApiErrors(error, false)
      })
      .addCase(createAsset.rejected, (state, error) => {
        state.status = LoadingStatus.failed
        handleApiErrors(error, false)
      })
      .addCase(editAsset.fulfilled, (state, action) => {
        const updated = action.payload as IAsset

        if (updated?.id && state.asset?.id === updated.id) {
          state.asset = { ...state.asset, ...updated }
        }
      })
      .addCase(editAsset.rejected, (state, error) => {
        state.status = LoadingStatus.failed
        const payload = error.payload as
          | { code?: string; statusCode?: number; message?: unknown }
          | undefined
        const nestedCode =
          payload?.message &&
          typeof payload.message === 'object' &&
          !Array.isArray(payload.message)
            ? (payload.message as { code?: string }).code
            : undefined

        if (
          payload?.code === 'ASSET_EDIT_CONFLICT' ||
          nestedCode === 'ASSET_EDIT_CONFLICT' ||
          payload?.code === 'ASSET_LOCKED' ||
          nestedCode === 'ASSET_LOCKED' ||
          payload?.statusCode === 409
        ) {
          return
        }
        handleApiErrors(error, false)
      })
      .addCase(setAssetBlocked.fulfilled, (state, action) => {
        const updated = action.payload as IAsset

        if (updated?.id && state.asset?.id === updated.id) {
          state.asset = { ...state.asset, ...updated }
        }
      })
      .addCase(setAssetBlocked.rejected, (state, error) => {
        state.status = LoadingStatus.failed
        handleApiErrors(error, false)
      })
      .addCase(assignAssetAllowedEditor.fulfilled, (state, action) => {
        const updated = action.payload as IAsset

        if (updated?.id && state.asset?.id === updated.id) {
          state.asset = { ...state.asset, ...updated }
        }
      })
      .addCase(assignAssetAllowedEditor.rejected, (state, error) => {
        state.status = LoadingStatus.failed
        handleApiErrors(error, false)
      })
      .addCase(acceptDraftAsset.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(acceptDraftAsset.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        const assetId = action.meta.arg

        if (state.asset?.id === assetId) {
          state.asset = { ...state.asset, isDraft: false }
        }

        if (Array.isArray(state.assets) && assetId) {
          state.assets = state.assets.map(a =>
            a.id === assetId ? { ...a, isDraft: false } : a
          )
        }
      })
      .addCase(acceptDraftAsset.rejected, (state, error) => {
        state.status = LoadingStatus.failed
        handleApiErrors(error, false)
      })
      .addCase(copyAsset.rejected, (state, error) => {
        state.status = LoadingStatus.failed
        handleApiErrors(error, false)
      })
      .addCase(deleteAsset.rejected, (state, error) => {
        state.status = 'failed'
        handleApiErrors(error, false)
      })
      .addCase(getProductsCategoriesNames.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getProductsCategoriesNames.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.productsCategoriesNames = action.payload
      })
      .addCase(getProductsCategoriesNames.rejected, (state, error) => {
        state.status = LoadingStatus.failed
        handleApiErrors(error, false)
      })
      .addCase(getProductsCategoriesAssets.pending, state => {
        state.status = LoadingStatus.loading
        state.productsCategoriesAssets = INITIAL_PRODUCTS_CATEGORIES_ASSETS
      })
      .addCase(getProductsCategoriesAssets.fulfilled, (state, action) => {
        if (
          action.payload.requestSeq !== getProductsCategoriesAssetsRequestSeq
        ) {
          return
        }
        state.status = LoadingStatus.loaded
        state.productsCategoriesAssets = action.payload.data
      })
      .addCase(getProductsCategoriesAssets.rejected, (state, error) => {
        state.status = LoadingStatus.failed
        state.productsCategoriesAssets = INITIAL_PRODUCTS_CATEGORIES_ASSETS
        handleApiErrors(error, false)
      })
      .addCase(clearEditedAssetOnRedux, state => {
        state.asset = INITIAL_ASSET
      })
  }
})

export const selectAssets = (state: RootState) => state.assets
export const selectAssetsStatus: (state: RootState) => TLoadingStatus =
  createSelector(selectAssets, assetsState => assetsState.status)
export const selectAssetsListStatus: (state: RootState) => TLoadingStatus =
  createSelector(selectAssets, assetsState => assetsState.listStatus)

export const selectGetAssets: (state: RootState) => IAsset[] = createSelector(
  selectAssets,
  // assets => assets.assets
  pathOr([], ['assets'])
)
export const selectAssetsPagination: (state: RootState) => IPagination =
  createSelector(
    selectAssets,
    pathOr(
      {
        page: 0,
        pagesTotal: 0,
        recordsTotal: 0,
        take: 0
      },
      ['pagination']
    )
  )

export const selectBrands: (state: RootState) => IBrand[] = createSelector(
  selectAssets,
  pathOr([], ['brands'])
)

export const selectAssetsTypes: (state: RootState) => IAssetsTypes[] =
  createSelector(selectAssets, pathOr([], ['assetsTypes']))

export const selectGetAsset: (state: RootState) => IAsset | undefined =
  createSelector(selectAssets, pathOr(undefined, ['asset']))

export const selectGetProductsCategoriesNames: (state: RootState) => string[] =
  createSelector(selectAssets, pathOr([], ['productsCategoriesNames']))

export const selectGetProductsCategoriesAssets: (
  state: RootState
) => IAssetsProductCategories = createSelector(
  selectAssets,
  assets => assets.productsCategoriesAssets
)

export default assetsSlice.reducer
